<template>
  <div>
    <div v-if="loading" class="flex flex-col items-center justify-center py-8">
      <div class="h-7 w-7 animate-spin rounded-full border-2 border-slate-600 border-t-emerald-400"></div>
      <p class="mt-3 text-xs text-slate-400">Loading injuries…</p>
    </div>

    <div v-else-if="error" class="px-3 py-4 text-center">
      <span class="inline-block rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
        {{ error }}
      </span>
    </div>

    <div v-else class="space-y-3">
      <!-- Which team's injury picture is worse. The bar reads left = away,
           right = home, matching every other row on the card. -->
      <div v-if="hasAnyInjury">
        <div class="flex h-1.5 overflow-hidden rounded-full bg-white/5">
          <div class="bg-slate-400/70" :style="{ width: lean.awayShare * 100 + '%' }"></div>
          <div class="flex-1 bg-slate-500/40"></div>
        </div>
        <p class="mt-1.5 text-center text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
          <template v-if="lean.lean">Injuries lean {{ lean.lean }}</template>
          <template v-else>Injuries roughly even</template>
        </p>
      </div>

      <div class="grid gap-3 sm:grid-cols-2">
        <section v-for="col in columns" :key="col.abbr" class="min-w-0">
          <header class="mb-1.5 flex items-center gap-2 border-b border-white/10 pb-1.5">
            <img
              v-if="col.logo"
              :src="col.logo"
              :alt="col.abbr"
              class="h-5 w-5 shrink-0"
              @error="$event.target.style.display = 'none'"
            />
            <span class="text-xs font-bold text-white">{{ col.abbr }}</span>
            <span class="text-[11px] text-slate-500">
              {{ col.players.length }} {{ col.players.length === 1 ? "injury" : "injuries" }}
            </span>
          </header>

          <p v-if="!col.players.length" class="py-2 text-[11px] text-slate-500">
            No injuries reported.
          </p>

          <ul v-else class="space-y-1.5">
            <li
              v-for="player in col.shown"
              :key="player.name"
              class="rounded-lg bg-white/[0.03] px-2 py-1.5"
            >
              <div class="flex items-center gap-2">
                <img
                  v-if="player.headshot && !brokenHeadshots.has(player.name)"
                  :src="player.headshot"
                  :alt="player.name"
                  class="h-8 w-8 shrink-0 rounded-full bg-white/[0.06] object-cover"
                  loading="lazy"
                  @error="markHeadshotBroken(player.name)"
                />
                <span
                  class="w-8 shrink-0 rounded px-1 py-0.5 text-center text-[9px] font-black uppercase"
                  :class="tierClass(player.position)"
                >
                  {{ player.position || "—" }}
                </span>
                <span class="min-w-0 flex-1 truncate text-xs font-semibold text-slate-100">
                  {{ player.name }}
                </span>
                <span
                  class="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase"
                  :class="statusClass(player.status)"
                >
                  {{ player.status }}
                </span>
              </div>
              <p
                v-if="player.type || player.shortComment"
                class="mt-1 text-[11px] text-slate-500"
                :class="expanded.has(player.name) ? '' : 'line-clamp-1'"
                @click="toggle(player.name)"
              >
                <span v-if="player.type" class="font-semibold text-slate-400">{{ player.type }}</span>
                <span v-if="player.type && player.shortComment"> · </span>
                <span v-if="player.shortComment">{{ player.shortComment }}</span>
              </p>
            </li>
          </ul>

          <button
            v-if="col.players.length > col.shown.length"
            type="button"
            class="mt-1.5 text-[11px] font-semibold text-emerald-400 hover:text-emerald-300"
            @click="showAll = true"
          >
            +{{ col.players.length - col.shown.length }} more
          </button>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/utils/api";
import { teamLogo } from "@/utils/teamLogo.js";
import { rankInjuries, weightTier, directionalLean } from "@/utils/injuryImpact.js";

const STATUS_TINTS = [
  [/(out|\bir\b|injured reserve|reserve|pup|inactive)/, "bg-red-500/20 text-red-300"],
  [/(doubtful|questionable)/, "bg-yellow-500/20 text-yellow-200"],
  [/(probable|likely|limited|day.?to.?day)/, "bg-orange-500/20 text-orange-200"],
];

const TIER_TINTS = {
  high: "bg-red-500/25 text-red-200",
  mid: "bg-amber-500/20 text-amber-200",
  low: "bg-slate-500/25 text-slate-300",
  minimal: "bg-white/5 text-slate-500",
};

export default {
  name: "InjuryReport",
  props: {
    game: { type: Object, required: true },
    // Rows per team on the card peek; unset on the full page.
    limit: { type: Number, default: null },
  },
  data() {
    return {
      loading: false,
      error: null,
      fetched: null, // { home: [...], away: [...] } when self-fetched
      showAll: false,
      expanded: new Set(),
      brokenHeadshots: new Set(),
    };
  },
  computed: {
    // The detail endpoint folds injuries onto the game (with the ESPN blurb);
    // the list payload does not, so the card peek fetches them itself.
    fromSummary() {
      return this.game.summary?.injuries || null;
    },
    source() {
      return this.fromSummary || this.fetched || { home: [], away: [] };
    },
    homeRanked() {
      return rankInjuries(this.source.home);
    },
    awayRanked() {
      return rankInjuries(this.source.away);
    },
    hasAnyInjury() {
      return this.homeRanked.length > 0 || this.awayRanked.length > 0;
    },
    lean() {
      return directionalLean(
        this.source.home,
        this.source.away,
        this.game.homeTeam.abbr,
        this.game.awayTeam.abbr
      );
    },
    columns() {
      const cap = (players) =>
        this.limit != null && !this.showAll ? players.slice(0, this.limit) : players;

      return [
        {
          abbr: this.game.awayTeam.abbr,
          logo: this.logoFor(this.game.awayTeam),
          players: this.awayRanked,
          shown: cap(this.awayRanked),
        },
        {
          abbr: this.game.homeTeam.abbr,
          logo: this.logoFor(this.game.homeTeam),
          players: this.homeRanked,
          shown: cap(this.homeRanked),
        },
      ];
    },
  },
  methods: {
    logoFor(team) {
      return team.logo || teamLogo(team.abbr);
    },
    tierClass(position) {
      return TIER_TINTS[weightTier(position)];
    },
    statusClass(status) {
      const text = String(status || "").toLowerCase();
      for (const [pattern, klass] of STATUS_TINTS) {
        if (pattern.test(text)) return klass;
      }
      return "bg-slate-500/20 text-slate-300";
    },
    markHeadshotBroken(name) {
      const next = new Set(this.brokenHeadshots);
      next.add(name);
      this.brokenHeadshots = next;
    },
    toggle(name) {
      // Set is not reactive on its own; swap it so Vue re-renders.
      const next = new Set(this.expanded);
      next.has(name) ? next.delete(name) : next.add(name);
      this.expanded = next;
    },
    async fetchInjuries() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await api.get("/api/v1/injuries/", {
          params: {
            home_team_id: this.game.homeTeam.id,
            away_team_id: this.game.awayTeam.id,
          },
        });
        this.fetched = {
          home: (data.home_injuries || []).map(this.normalize),
          away: (data.away_injuries || []).map(this.normalize),
        };
      } catch (err) {
        this.error = "Couldn't load injuries.";
      } finally {
        this.loading = false;
      }
    },
    // The /api/v1/injuries feed nests status under `injury` and has no blurb;
    // its `injury.type` is an ESPN status enum object, so the readable body
    // part is `injury.details.type` ("Hamstring"). Flatten to the summary shape.
    normalize(player) {
      return {
        name: player.name,
        position: player.position,
        status: player.injury?.status,
        type: player.injury?.details?.type || null,
        shortComment: null,
        headshot: player.headshot,
      };
    },
  },
  created() {
    if (!this.fromSummary) this.fetchInjuries();
  },
};
</script>
